import React, {Component, ReactElement} from 'react'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'
import {SortOrder} from '../../../components/shared/SortOrder'
import {COMMON_CONSTANTS} from '../../../CommonConstants'
import ListSearchFormArea from '../../../styledComponents/adminPage/ListSearchFormArea'
import {AdminDefaultMediumButton} from '../../../styledComponents/adminPage/Buttons'
import SearchPageActionArea from '../../../styledComponents/adminPage/SearchPageActionArea'
import {PageMainArea} from '../../../components/shared/hoc/withAdminPageTemplate'
import {SearchResultCountDisplayProps} from '../../../styledComponents/adminPage/SearchResultCountDisplay'
import SortableTableHeader, {
    SortableTableHeaderCell,
} from '../../../styledComponents/adminPage/SortablePaginationTableHeader'
import PPTextField from '../../../components/shared/forms/PPTextField'
import {Messages} from '../../../Messages'
import {formatDateTimeForUpdateDate} from '../../../formatters/formatDate'
import SortablePaginationTable from '../../../styledComponents/adminPage/SortablePaginationTable'
import SortablePaginationTableBody from '../../../styledComponents/adminPage/SortablePaginationTableBody'
import {HKConstants} from '../../../HKConstants'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {
    ConditionButtonCell,
    ConditionCategoryCell,
    ConditionColumNameCell,
    ConditionInputCell,
    ConditionTable,
    ConditionTableBody,
    ConditionTableRow,
} from '../../../styledComponents/adminPage/ConditionTable'
import {HeadingAreaMessageParams} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import NatAreaCodeRepo from './repository/NatAreaCodeRepo'
import {
    AreaCodeInfo,
    NatGetAreaCodeList,
    SearchCondition,
    SearchConditionPageInfo,
    SearchResult,
} from './repository/NatGetAreaCodeList'
import NatCurrentDateTimeRepo from '../currentDateTime/repository/NatCurrentDateTimeRepo'
import {Location} from 'react-router-dom'

import {Link, TableCell, TableRow} from '@mui/material'

const KEY_SEARCH_CONDITION: string = 'adminAreaCodeListPageSearchCondition'

interface Props {
    areaCodeRepo: NatAreaCodeRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    searchCondition: SearchCondition
    searchConditionPageInfo: SearchConditionPageInfo
    searchConditionSortInfo: SortOrder[]
    areaCodeInfo: AreaCodeInfo[]
    searchResult: SearchResult
    isLoadCompleted: boolean
}

interface LastSearchCondition extends SearchCondition {
    pageNum?: number
}

class NatAdminAreaCodeListPage extends Component<Props & AdminPagePropsSet, States> {

    subSortKey: SortOrder = new SortOrder('loginBeforeMessageId', 'asc')

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        // 前回検索条件が保持されていればデフォルト値として設定する
        let lastSearchCondition: LastSearchCondition = {}
        const sessionStorageSearchCondition: string | null = window.sessionStorage.getItem(KEY_SEARCH_CONDITION)
        if (sessionStorageSearchCondition) {
            lastSearchCondition = JSON.parse(sessionStorageSearchCondition)
        }

        let defaultSearchCondition: SearchCondition = {}
        if (Object.keys(lastSearchCondition).length) {
            defaultSearchCondition = {
                areaCode: lastSearchCondition.areaCode,
                areaName: lastSearchCondition.areaName,
                contactMailAddress: lastSearchCondition.contactMailAddress,
            }
        }

        let defaultSearchConditionPage: SearchConditionPageInfo = {
            pageNum: COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[0],
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }
        if (lastSearchCondition.pageNum) {
            defaultSearchConditionPage = {
                pageNum: Number(lastSearchCondition.pageNum),
                page: COMMON_CONSTANTS.FIRST_PAGE,
            }
        }


        this.state = {
            searchCondition: defaultSearchCondition,
            searchConditionPageInfo: defaultSearchConditionPage,
            searchConditionSortInfo: [
                new SortOrder('updateDatetime', 'desc'),
                new SortOrder('areaCode', 'asc'),
            ],
            areaCodeInfo: [],
            searchResult: {},
            isLoadCompleted: false,
        }

        this.handleChangePage = this.handleChangePage.bind(this)
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this)
        this.onChangeAreaCode = this.onChangeAreaCode.bind(this)
        this.onChangeAreaName = this.onChangeAreaName.bind(this)
        this.onChangeContactMailAddress = this.onChangeContactMailAddress.bind(this)
        this.onClickSearchButton = this.onClickSearchButton.bind(this)
    }

    async componentDidMount(): Promise<void> {

        await this.props.eventEmitter.showLoading()
        try {
            // CSRFトークンを設定するために、GETのAPIを実行
            await this.props.asyncCallFunction(
                this.props.currentDateTimeRepo.getCurrentDateTime.bind(this.props.currentDateTimeRepo),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            await this.searchAreaCode(
                this.state.searchCondition,
                this.state.searchConditionPageInfo,
                this.state.searchConditionSortInfo,
            )
            this.showInitialMessage()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    async searchAreaCode(
        searchCondition?: SearchCondition,
        searchConditionPageInfo?: SearchConditionPageInfo,
        searchConditionSortInfo?: SortOrder[],
    ): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            // 地域コード一覧取得
            const response: Nullable<NatGetAreaCodeList> =
                await this.props.asyncCallFunction(
                    this.props.areaCodeRepo.getAreaCodeList.bind(
                        this.props.areaCodeRepo,
                        searchCondition,
                        searchConditionPageInfo,
                        searchConditionSortInfo,
                    ),
                    (httpErrorOrNetworkError) => {
                        this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )

            if (response && response.details) {
                this.setState({
                    areaCodeInfo: response.details.areaCodeInfo,
                    searchResult: response.details.searchResult,
                })
            }

        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.beforeUserAction()

        // 検索条件を保持
        const lastSearchCondition: LastSearchCondition = {
            areaCode: this.state.searchCondition.areaCode,
            areaName: this.state.searchCondition.areaName,
            contactMailAddress: this.state.searchCondition.contactMailAddress,
            pageNum: Number(event.target.value),
        }
        SessionStorageHelper.setItem(KEY_SEARCH_CONDITION, JSON.stringify(lastSearchCondition))

        const searchConditionPageInfo = this.state.searchConditionPageInfo
        searchConditionPageInfo.pageNum = Number(event.target.value)
        searchConditionPageInfo.page = COMMON_CONSTANTS.FIRST_PAGE
        await this.searchAreaCode(
            this.state.searchCondition,
            searchConditionPageInfo,
            this.state.searchConditionSortInfo,
        )
        this.setState({searchConditionPageInfo: searchConditionPageInfo})
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    async handleChangePage(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): Promise<void> {
        this.beforeUserAction()
        const searchConditionPageInfo: SearchConditionPageInfo = this.state.searchConditionPageInfo
        searchConditionPageInfo.page = newPage
        await this.searchAreaCode(
            this.state.searchCondition,
            searchConditionPageInfo,
            this.state.searchConditionSortInfo,
        )
        this.setState({searchConditionPageInfo: searchConditionPageInfo})
    }


    renderAreaCodeInfoTable(areaCodeInfo: AreaCodeInfo[]): Nullable<ReactElement> {
        if (areaCodeInfo.length === 0) {
            return null
        }
        const headers: SortableTableHeaderCell[] = [
            {id: 'areaCode', label: '地域コード'},
            {id: 'areaName', label: '地域名'},
            {id: 'contactMailAddress', label: '連絡先メールアドレス'},
            {id: 'updateDatetime', label: '更新日時'},
        ]

        return (
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.searchResult.total ? this.state.searchResult.total : 0,
                    rowsPerPage:
                        this.state.searchConditionPageInfo.pageNum ? this.state.searchConditionPageInfo.pageNum : 0,
                    page: this.state.searchConditionPageInfo.page ? this.state.searchConditionPageInfo.page : 0,
                    onPageChange: this.handleChangePage.bind(this),
                    onRowsPerPageChange: this.handleChangeRowsPerPage.bind(this),
                }}
            >
                <SortableTableHeader
                    noWrap
                    headers={headers}
                />
                <SortablePaginationTableBody>
                    {this.state.areaCodeInfo.map(areaCodeRecord => this.renderAreaCodeInfoRecord(areaCodeRecord))}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        )
    }

    renderAreaCodeInfoRecord(areaCodeInfo: AreaCodeInfo): ReactElement {
        const areaCodePageUrl: string = `/admin/areaCode/${areaCodeInfo.areaId}`
        return (
            <TableRow key={areaCodeInfo.areaCode}>
                <TableCell align={'center'}><Link href={areaCodePageUrl}
                                                  underline="hover">{areaCodeInfo.areaCode}</Link></TableCell>
                <TableCell align={'center'}>{areaCodeInfo.areaName}</TableCell>
                <TableCell align={'center'}>{areaCodeInfo.contactMailAddress}</TableCell>
                <TableCell align={'center'}>{formatDateTimeForUpdateDate(areaCodeInfo.updateDatetime)}</TableCell>
            </TableRow>
        )
    }

    handleNewAreaCodeButtonClick(): void {
        this.props.navigate(`/admin/areaCodeRegister/`)
    }

    searchResults(): SearchResultCountDisplayProps {
        if (this.state.areaCodeInfo.length === 0) {
            if (!Object.keys(this.state.searchCondition).length) {
                return {noResultMessage: Messages.IOPCC0015.toString('地域コード')}
            }
            return {noResultMessage: Messages.IOPCC0014.toString('')}
        }

        return {
            totalCount: this.state.searchResult.total ? this.state.searchResult.total : undefined,
            rowsPerPage: this.state.searchConditionPageInfo.pageNum,
            page: this.state.searchConditionPageInfo.page,
        }
    }

    onChangeAreaCode(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchConditions = this.state.searchCondition
        searchConditions.areaCode = event.target.value
        this.setState({searchCondition: searchConditions})
    }

    onChangeAreaName(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchConditions = this.state.searchCondition
        searchConditions.areaName = event.target.value
        this.setState({searchCondition: searchConditions})
    }

    onChangeContactMailAddress(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchConditions = this.state.searchCondition
        searchConditions.contactMailAddress = event.target.value
        this.setState({searchCondition: searchConditions})
    }

    async onClickSearchButton(): Promise<void> {
        this.beforeUserAction()
        // 検索条件を保持
        const lastSearchCondition: LastSearchCondition = {
            areaCode: this.state.searchCondition.areaCode,
            areaName: this.state.searchCondition.areaName,
            contactMailAddress: this.state.searchCondition.contactMailAddress,
            pageNum: this.state.searchConditionPageInfo.pageNum,
        }
        SessionStorageHelper.setItem(KEY_SEARCH_CONDITION, JSON.stringify(lastSearchCondition))

        const searchConditionPageInfo = this.state.searchConditionPageInfo
        searchConditionPageInfo.page = COMMON_CONSTANTS.FIRST_PAGE
        await this.searchAreaCode(
            this.state.searchCondition,
            searchConditionPageInfo,
            this.state.searchConditionSortInfo)

        this.setState({searchConditionPageInfo: searchConditionPageInfo})
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return (
            <>
                <ListSearchFormArea
                    searchResult={this.searchResults()}
                    onEnterKey={this.onClickSearchButton.bind(this)}
                >
                    <ConditionTable>
                        <ConditionTableBody>
                            <ConditionTableRow>
                                <ConditionCategoryCell rowSpan={3}>地域コード情報</ConditionCategoryCell>
                                <ConditionColumNameCell>地域コード</ConditionColumNameCell>
                                <ConditionInputCell colSpan={2}>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 2,
                                            },
                                        }}
                                        fullWidth
                                        onChange={this.onChangeAreaCode.bind(this)}
                                        defaultValue={this.state.searchCondition.areaCode}
                                        autoFocus
                                    />
                                </ConditionInputCell>
                                <ConditionButtonCell></ConditionButtonCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>地域名</ConditionColumNameCell>
                                <ConditionInputCell colSpan={2}>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 50,
                                            },
                                        }}
                                        fullWidth
                                        onChange={this.onChangeAreaName.bind(this)}
                                        defaultValue={this.state.searchCondition.areaName}
                                    />
                                </ConditionInputCell>
                                <ConditionButtonCell></ConditionButtonCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>連絡先メールアドレス</ConditionColumNameCell>
                                <ConditionInputCell colSpan={2}>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 100,
                                            },
                                        }}
                                        fullWidth
                                        onChange={this.onChangeContactMailAddress.bind(this)}
                                        defaultValue={this.state.searchCondition.contactMailAddress}
                                    />
                                </ConditionInputCell>
                                <ConditionButtonCell>
                                    <AdminDefaultMediumButton
                                        onClick={this.onClickSearchButton}>
                                        地域コードを検索する
                                    </AdminDefaultMediumButton>
                                </ConditionButtonCell>
                            </ConditionTableRow>
                        </ConditionTableBody>
                    </ConditionTable>
                </ListSearchFormArea>
                <SearchPageActionArea>
                    <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_AREA_CODE}>
                        <AdminDefaultMediumButton onClick={this.handleNewAreaCodeButtonClick.bind(this)}>
                            新規
                        </AdminDefaultMediumButton>
                    </AuthorityChecker>
                </SearchPageActionArea>
                <PageMainArea>
                    {this.renderAreaCodeInfoTable(this.state.areaCodeInfo)}
                </PageMainArea>
            </>
        )
    }
}

export default createAdminPage(NatAdminAreaCodeListPage)
